import React, { useState, useEffect } from "react";
import { useLocation, Link, useNavigate } from "react-router-dom";
import { Auth } from "aws-amplify";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import SetupForm from "./views/SetupForm.js";
import { getAccessToken } from "../../services/getAccessToken";
import FetchPropertyDetails from "./services/FetchPropertyDetails";
import NotFoundException from "../../utils/exception/NotFoundException";
import Unauthorized from "../../utils/exception/Unauthorized";
import spinner from "../../images/spinnner.gif";
import DateFormatterDD_MM_YYYY from "../../utils/DateFormatterDD_MM_YYYY";
import Calender from "@mui/icons-material/CalendarTodayOutlined";
import People from "@mui/icons-material/PeopleAltOutlined";
import Back from "@mui/icons-material/KeyboardBackspace";
import IosShareIcon from "@mui/icons-material/IosShare";
import ShareModal from "./components/ShareModal";
import publicKeys from "../../utils/const/publicKeys.json";
import { resolvePrimaryAccommodationImageUrl } from "../../utils/accommodationImage";
import CancellationPolicySection from "./CancellationPolicySection";

const stripePromise = loadStripe(publicKeys.STRIPE_PUBLIC_KEYS.LIVE);
const DATE_KEY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;
export const BOOKINGS_ENDPOINT = "https://92a7z9y2m5.execute-api.eu-north-1.amazonaws.com/development/bookings";

export const normalizeBookingDateForRequest = (value) => {
  const normalized = String(value ?? "").trim();
  if (DATE_KEY_PATTERN.test(normalized)) {
    return normalized;
  }

  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? String(numericValue) : normalized;
};

export const buildBookingRequestEvent = ({ propertyId, bookingDetails, userName }) => ({
  identifiers: {
    property_Id: propertyId,
  },
  general: {
    guests: Number.parseFloat(bookingDetails.guests),
    latePayment: false,
    arrivalDate: normalizeBookingDateForRequest(bookingDetails.checkInDate),
    departureDate: normalizeBookingDateForRequest(bookingDetails.checkOutDate),
    guestName: userName,
  },
});

export const buildBookingPostRequest = ({ event, authToken }) => ({
  url: BOOKINGS_ENDPOINT,
  options: {
    method: "POST",
    body: JSON.stringify(event),
    headers: {
      Authorization: authToken,
      "Content-Type": "application/json; charset=UTF-8",
    },
  },
});

export const parseBookingResponseBody = async (response) => {
  const responseText = await response.text();
  if (!responseText.trim()) {
    return null;
  }

  try {
    return JSON.parse(responseText);
  } catch {
    return { message: responseText };
  }
};

export const getBookingErrorMessage = (response, responseBody) =>
  responseBody?.message ||
  responseBody?.error ||
  responseBody?.response?.message ||
  `HTTP error! Status: ${response.status}`;

const BookingOverview = () => {
  const navigate = useNavigate();
  const [bookingDetails, setBookingDetails] = useState(null);
  const [userName, setUserName] = useState(null);
  const [showCheckout, setShowCheckout] = useState(null);
  const [hideButton, setHideButton] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [bookingId, setBookingId] = useState(null);
  const [pricingObject, setPricingObject] = useState(null);
  const [isProcessing, setIsProcessing] = useState(true);
  const [stripeClientSecret, setStripeClientSecret] = useState(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const propertyId = searchParams.get("id");
  const checkInDate = searchParams.get("checkInDate");
  const checkOutDate = searchParams.get("checkOutDate");
  const guests = searchParams.get("guests");
  const showAuthenticationPrompt = !isAuthenticated;
  const isTestBooking = Boolean(bookingDetails?.testStatus);
  const isInquiryListing = pricingObject?.bookingType === "inquiry";
  const shouldShowConfirmButton = !hideButton && !isTestBooking && !isInquiryListing;
  const shouldShowCheckout = !isTestBooking && !isInquiryListing && Boolean(showCheckout && stripeClientSecret && bookingId);

  useEffect(() => {
    const fetchAccommodation = async () => {
      try {
        if (!checkInDate || !checkOutDate || !propertyId || !guests) {
          setError("Unable to retrieve property information from the URL. Please try again later.");
          console.error("URL Query Parameters are missing from your request. Unable to load BookingOverview.");
          throw new NotFoundException("checkInDate, checkOutDate, guests, or PropertyId missing from URL.");
        }

        const retrievedPricingObject = await FetchPropertyDetails(propertyId, checkInDate, checkOutDate);
        setPricingObject(retrievedPricingObject);
        const retrievedBookingDetails = {
          accommodation: retrievedPricingObject,
          checkInDate,
          checkOutDate,
          guests,
          testStatus: Boolean(retrievedPricingObject?.testStatus),
        };

        setBookingDetails(retrievedBookingDetails);

        if (!retrievedBookingDetails) {
          setError("Booking details or pricing object is not available.");
          throw new NotFoundException("Booking details or pricing object is not available.");
        }
      } catch (error) {
        console.error("Error fetching accommodation data:", error);
        setError(
          "We couldn't load the property details. Check your internet or try again. If this continues, reach out to support."
        );
      }
    };
    fetchAccommodation();
  }, [propertyId, checkInDate, checkOutDate, guests]);

  useEffect(() => {
    const checkAuthentication = async () => {
      try {
        const authUser = await Auth.currentAuthenticatedUser();
        setIsAuthenticated(true);

        const attrs = authUser.attributes || {};
        const name = attrs.given_name ?? attrs.name ?? "";
        setUserName(name);
      } catch {
        setIsAuthenticated(false);
      }
    };
    checkAuthentication();
  }, [location.key, location.search]);

  if (error) {
    return <div className="error-message">{error}</div>;
  } else if (!bookingDetails || !pricingObject) {
    return <img src={spinner} alt="Loading circle spinner" className="centerObject"></img>;
  }

  const createBooking = async () => {
    const event = buildBookingRequestEvent({ propertyId, bookingDetails, userName });

    try {
      const authToken = getAccessToken();
      if (!authToken) {
        setError("Missing authentication token. Try refreshing the page.");
        throw new Unauthorized("Unable to get a valid authentication token.");
      }

      const bookingRequest = buildBookingPostRequest({ event, authToken });
      const request = await fetch(bookingRequest.url, bookingRequest.options);
      const response = await parseBookingResponseBody(request);
      if (!request.ok) {
        throw new Error(getBookingErrorMessage(request, response));
      }

      if (response?.stripeClientSecret && response?.bookingId) {
        setBookingId(response.bookingId);
        setStripeClientSecret(response.stripeClientSecret);
      } else {
        console.error("Failed to save booking. Please contact support.");
        throw new NotFoundException(
          "POST request towards backend failed. Check the Network tab to debug the request. Booking process failed."
        );
      }
      setShowCheckout(true);
    } catch (error) {
      console.error("Error creating booking:", error);
      setError("Unable to store booking, please contact the support team or try again later.");
    }
  };

  const handleConfirmAndPay = async () => {
    try {
      setIsProcessing(true);
      setLoading(true);
      await createBooking();
      setHideButton(true);
    } catch (error) {
      setLoading(false);
      setError("Failed to initialize checkout page. Contact support or try again.");
      console.error("Something went wrong during handling the confirm and pay button.", error);
    }
  };

  const handleRequestInfo = () => {
    navigate(`/listingdetails?ID=${propertyId}`);
  };

  const handleRequestToBook = async () => {
    try {
      setIsProcessing(true);
      setLoading(true);
      const authToken = getAccessToken();
      if (!authToken) {
        setError("Missing authentication token. Try refreshing the page.");
        return;
      }
      const event = buildBookingRequestEvent({ propertyId, bookingDetails, userName });
      const bookingRequest = buildBookingPostRequest({ event, authToken });
      const request = await fetch(bookingRequest.url, bookingRequest.options);
      const response = await parseBookingResponseBody(request);
      if (!request.ok) {
        throw new Error(getBookingErrorMessage(request, response));
      }
      if (!response?.bookingId) {
        throw new NotFoundException(
          "POST request towards backend failed. Check the Network tab to debug the request. Inquiry process failed."
        );
      }
      navigate(`/bookingconfirmationoverview?bookingId=${response.bookingId}&status=inquiry`);
    } catch (error) {
      console.error("Error sending inquiry:", error);
      setError("Unable to send your request. Please try again later.");
    } finally {
      setLoading(false);
      setIsProcessing(false);
    }
  };

  return (
    <main className="booking-container" style={{ cursor: isProcessing ? "wait" : "default" }}>
      {error && <div className="error-message">{error}</div>}
      <div className="booking-header">
        <div className="goBackButton">
          <Link to={`/listingdetails?ID=${propertyId}`}>
            <Back />
          </Link>
        </div>
        <h1>Booking Overview</h1>
        <button className="booking-share-btn" onClick={() => setShowShareModal(true)} aria-label="Share property">
          <IosShareIcon fontSize="small" />
        </button>
      </div>

      {showShareModal && (
        <ShareModal
          url={`${globalThis.location.origin}/listingdetails?ID=${propertyId}`}
          title={pricingObject?.title}
          onClose={() => setShowShareModal(false)}
        />
      )}

      <div className="Bookingcontainer">
        <div className="right-panel">
          <div>Your Journey</div>
          <div className="booking-details">
            <div className="detail-row">
              <span className="detail-label">
                <Calender /> Date:
              </span>
              <span className="detail-value">
                {DateFormatterDD_MM_YYYY(bookingDetails.checkInDate)} -{" "}
                {DateFormatterDD_MM_YYYY(bookingDetails.checkOutDate)}
              </span>
            </div>
            <div className="detail-row">
              <span className="detail-label">
                <People /> Guests:
              </span>
              <span className="detail-value">{bookingDetails.guests} guests</span>
            </div>
          </div>

          <CancellationPolicySection rules={pricingObject?.rules} />

          {showAuthenticationPrompt ? (
            <div>
              <h2>Please Register or Log In to Continue</h2>
              <div className="auth-actions">
                <Link to={`/login?redirect=${encodeURIComponent(location.pathname + location.search)}`}>
                  <button className="login-button">Login</button>
                </Link>
                <Link to={`/register?redirect=${encodeURIComponent(location.pathname + location.search)}`}>
                  <button className="register-button">Register</button>
                </Link>
              </div>
            </div>
          ) : (
            <>
              {shouldShowConfirmButton && (
                <button type="submit" className="confirm-pay-button" onClick={handleConfirmAndPay} disabled={loading}>
                  {loading ? "Loading..." : "Confirm & Pay"}
                </button>
              )}
              {isInquiryListing && !hideButton && (
                <button type="button" className="confirm-pay-button" onClick={handleRequestToBook} disabled={loading}>
                  {loading ? "Sending..." : "Request to Book"}
                </button>
              )}
              {isTestBooking && (
                <Link to="/contact" className="footer-links">
                  <button type="button" className="confirm-pay-button" onClick={handleRequestInfo} disabled={loading}>
                    Request Info
                  </button>
                </Link>
              )}
              {shouldShowCheckout && (
                <Elements
                  stripe={stripePromise}
                  options={{
                    clientSecret: stripeClientSecret,
                    appearance: {},
                  }}>
                  <SetupForm handleConfirmAndPay={handleConfirmAndPay} bookingId={bookingId} loading />
                </Elements>
              )}
            </>
          )}
        </div>

        <div className="booking-details-container">
          <div className="booking-header1">Booking Details</div>
          <div className="left-panel">
            <div className="booking-details-name">
              <img
                className="bookingDetailsImage"
                src={resolvePrimaryAccommodationImageUrl(pricingObject.images, "thumb")}
                alt="Accommodation"
              />
              <div>
                <h1 className="booking-title">{pricingObject.title}</h1>
                <span className="acco-title-span">
                  {pricingObject.city}, {pricingObject.country}
                </span>
              </div>
            </div>
            <hr />

            <div className="detail-row">
              <span className="detail-label">
                <b>Price info:</b>
              </span>
            </div>

            <div className="detail-row">
              <span className="detail-label">Host price:</span>
              <span className="detail-value">
                €{" "}
                {(
                  (pricingObject.roomRate * pricingObject.differenceInDays || 0) + (pricingObject.platformFee || 0)
                ).toFixed(2)}
              </span>
            </div>

            <div className="detail-row">
              <span className="detail-label">Cleaning fee:</span>
              <span className="detail-value">
                € {pricingObject.cleaning || 0} x {pricingObject.differenceInDays} nights
              </span>
              <span className="detail-value">
                € {(pricingObject.cleaning * pricingObject.differenceInDays || 0).toFixed(2)}
              </span>
            </div>

            <div className="detail-row total-price">
              <span className="detail-label">Total:</span>
              <span className="detail-value">€ {(pricingObject.total || 0).toFixed(2)}</span>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default BookingOverview;
